// src/context/gtm.js

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './auth';
import { usePermissions } from './permissions';
import { extractDateFromBigQueryTimestamp } from '../utils/gtm/gtmHelpers';
import { cacheData, getCachedData, clearCachedData } from '../utils/gtm/cacheData';
import { 
    fetchUsersFromApi,
    fetchAuditLogsFromApi,
    fetchAccountsFromApi,
    fetchPendingRequestsFromApi,
    fetchApprovedRequestsFromApi,
    fetchRejectedRequestsFromApi,
    fetchContainersFromApi,
    fetchContainerUsersFromApi,
    fetchPendingContainerRequestsFromApi,
    fetchApprovedContainerRequestsFromApi,
    fetchRejectedContainerRequestsFromApi,
    fetchContainerAuditLogsFromApi,
    createAccessBindingFromApi,
    removeAccessBindingFromApi,
    modifyAccessBindingFromApi,
    approvePendingRequestFromApi,
    rejectPendingRequestFromApi,
    createContainerAccessBindingFromApi,
    removeContainerAccessBindingFromApi,
    modifyContainerAccessBindingFromApi,
    approveContainerPendingRequestFromApi,
    rejectContainerPendingRequestFromApi
} from '../utils/gtm/gtmApi';

// Cache keys
const CACHE_KEYS = {
    USERS: 'gtm_users',
    AUDIT_LOGS: 'gtm_audit_logs',
    ACCOUNTS: 'gtm_accounts',
    CONTAINERS: 'gtm_containers',
    CONTAINER_USERS: 'gtm_container_users',
    CONTAINER_AUDIT_LOGS: 'gtm_container_audit_logs',
    PENDING_REQUESTS: 'gtm_pending_requests',
    APPROVED_REQUESTS: 'gtm_approved_requests',
    REJECTED_REQUESTS: 'gtm_rejected_requests',
    PENDING_CONTAINER_REQUESTS: 'gtm_pending_container_requests',
    APPROVED_CONTAINER_REQUESTS: 'gtm_approved_container_requests',
    REJECTED_CONTAINER_REQUESTS: 'gtm_rejected_container_requests'
};

// Create context
const GtmContext = createContext();

// Context provider component
export const GtmProvider = ({ children }) => {
    const { isAuthenticated, user } = useAuth();
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const hasGtmPermission = hasPermission('admin/gtm');

    // State for users in BigQuery
    const [users, setUsers] = useState([]);
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [usersError, setUsersError] = useState(null);

    // State for audit logs
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLogsLoaded, setAuditLogsLoaded] = useState(false);
    const [auditLogsError, setAuditLogsError] = useState(null);

    // State for GTM accounts
    const [accounts, setAccounts] = useState([]);
    const [accountsLoaded, setAccountsLoaded] = useState(false);
    const [accountsError, setAccountsError] = useState(null);

    // State for GTM containers
    const [containers, setContainers] = useState([]);
    const [containersLoaded, setContainersLoaded] = useState(false);
    const [containersError, setContainersError] = useState(null);

    // State for GTM container users
    const [containerUsers, setContainerUsers] = useState([]);
    const [containerUsersLoaded, setContainerUsersLoaded] = useState(false);
    const [containerUsersError, setContainerUsersError] = useState(null);

    // State for GTM container audit logs
    const [containerAuditLogs, setContainerAuditLogs] = useState([]);
    const [containerAuditLogsLoaded, setContainerAuditLogsLoaded] = useState(false);
    const [containerAuditLogsError, setContainerAuditLogsError] = useState(null);

    // State for GTM pending requests
    const [pendingRequests, setPendingRequests] = useState([]);
    const [pendingRequestsLoaded, setPendingRequestsLoaded] = useState(false);
    const [pendingRequestsError, setPendingRequestsError] = useState(null);

    // State for GTM approved requests
    const [approvedRequests, setApprovedRequests] = useState([]);
    const [approvedRequestsLoaded, setApprovedRequestsLoaded] = useState(false);
    const [approvedRequestsError, setApprovedRequestsError] = useState(null);

    // State for GTM rejected requests
    const [rejectedRequests, setRejectedRequests] = useState([]);
    const [rejectedRequestsLoaded, setRejectedRequestsLoaded] = useState(false);
    const [rejectedRequestsError, setRejectedRequestsError] = useState(null);

    // State for GTM pending container requests
    const [pendingContainerRequests, setPendingContainerRequests] = useState([]);
    const [pendingContainerRequestsLoaded, setPendingContainerRequestsLoaded] = useState(false);
    const [pendingContainerRequestsError, setPendingContainerRequestsError] = useState(null);

    // State for GTM approved container requests
    const [approvedContainerRequests, setApprovedContainerRequests] = useState([]);
    const [approvedContainerRequestsLoaded, setApprovedContainerRequestsLoaded] = useState(false);
    const [approvedContainerRequestsError, setApprovedContainerRequestsError] = useState(null);

    // State for GTM rejected container requests
    const [rejectedContainerRequests, setRejectedContainerRequests] = useState([]);
    const [rejectedContainerRequestsLoaded, setRejectedContainerRequestsLoaded] = useState(false);
    const [rejectedContainerRequestsError, setRejectedContainerRequestsError] = useState(null);

    // State to track context initialization
    const [initialized, setInitialized] = useState(false);

    // Refs to prevent duplicate API calls
    const isLoadingUsers = useRef(false);
    const isLoadingAuditLogs = useRef(false);
    const isLoadingAccounts = useRef(false);
    const isLoadingContainers = useRef(false);
    const isLoadingContainerUsers = useRef(false);
    const isLoadingContainerAuditLogs = useRef(false);
    const isLoadingPendingRequests = useRef(false);
    const isLoadingApprovedRequests = useRef(false);
    const isLoadingRejectedRequests = useRef(false);
    const isLoadingPendingContainerRequests = useRef(false);
    const isLoadingApprovedContainerRequests = useRef(false);
    const isLoadingRejectedContainerRequests = useRef(false);
    
    // Track if initial data loading has happened
    const hasLoadedInitialData = useRef(false);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching users from BigQuery using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the users
     * @returns {Promise<Array>} Promise resolving to an array of users
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchUsers = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingUsers.current) {
            return users;
        }

        // Check if we already have the data and can return it
        if (usersLoaded && users !== null && users.length > 0 && !forceRefresh) {
            return users;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedUsers = await getCachedData(CACHE_KEYS.USERS);
            if (cachedUsers && cachedUsers.length > 0) {
                setUsers(cachedUsers);
                setUsersLoaded(true);
                setUsersError(null);
                return cachedUsers;
            }
        }
        isLoadingUsers.current = true;
        
        const startTime = performance.now();
        try {
            const userData = await fetchUsersFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${userData.length} users in ${duration.toFixed(2)}ms`);

            // Update state
            setUsers(userData);
            setUsersLoaded(true);
            setUsersError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.USERS, userData);

            return userData;
        } catch (error) {
            if (error.response) {
                console.error(`[GTM Context]: Error fetching users (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GTM Context]: Error fetching users - No response received:', error.message);
            } else {
                console.error('[GTM Context]: Error fetching users:', error.message);
            }

            setUsersError(error.response?.data?.error?.text || error.message || 'Failed to fetch users data');
            return [];
        } finally {
            isLoadingUsers.current = false;
        }
    }, [users, usersLoaded]);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching audit logs using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the audit logs
     * @returns {Promise<Array>} Promise resolving to an array of audit logs
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchAuditLogs = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingAuditLogs.current) {
            return auditLogs;
        }

        // Check if we already have the data and can return it
        if (auditLogsLoaded && auditLogs !== null && !forceRefresh) {
            return auditLogs;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedLogs = await getCachedData(CACHE_KEYS.AUDIT_LOGS);
            if (cachedLogs && cachedLogs.length > 0) {
                setAuditLogs(cachedLogs);
                setAuditLogsLoaded(true);
                setAuditLogsError(null);
                return cachedLogs;
            }
        }
        isLoadingAuditLogs.current = true;

        const startTime = performance.now();
        try {
            const logsData = await fetchAuditLogsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${logsData.length} audit logs in ${duration.toFixed(2)}ms`);

            // Update state
            setAuditLogs(logsData);
            setAuditLogsLoaded(true);
            setAuditLogsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.AUDIT_LOGS, logsData);

            return logsData;
        } catch (error) {
            if (error.response) {
                console.error(`[GTM Context]: Error fetching audit logs (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GTM Context]: Error fetching audit logs - No response received:', error.message);
            } else {
                console.error('[GTM Context]: Error fetching audit logs:', error.message);
            }

            setAuditLogsError(error.response?.data?.error?.text || error.message || 'Failed to fetch audit logs');
            return [];
        } finally {
            isLoadingAuditLogs.current = false;
        }
    }, [auditLogs, auditLogsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching GTM accounts using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the GTM accounts
     * @returns {Promise<Array>} Promise resolving to an array of GTM accounts
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchAccounts = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingAccounts.current) {
            return accounts;
        }

        // Check if we already have the data and can return it
        if (accountsLoaded && accounts !== null && !forceRefresh) {
            return accounts;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedAccounts = await getCachedData(CACHE_KEYS.ACCOUNTS);
            if (cachedAccounts && cachedAccounts.length > 0) {
                setAccounts(cachedAccounts);
                setAccountsLoaded(true);
                setAccountsError(null);
                return cachedAccounts;
            }
        }

        // Set loading flag
        isLoadingAccounts.current = true;

        // Track performance
        const startTime = performance.now();

        try {
            const accountsData = await fetchAccountsFromApi(forceRefresh);

            // Log performance
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${accountsData.length} accounts in ${duration.toFixed(2)}ms`);

            // Update state
            setAccounts(accountsData);
            setAccountsLoaded(true);
            setAccountsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.ACCOUNTS, accountsData);

            return accountsData;
        } catch (error) {
            // Improved error logging with status code if available
            if (error.response) {
                console.error(`[GTM Context]: Error fetching accounts (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GTM Context]: Error fetching accounts - No response received:', error.message);
            } else {
                console.error('[GTM Context]: Error fetching accounts:', error.message);
            }

            setAccountsError(error.response?.data?.error?.text || error.message || 'Failed to fetch accounts');
            return [];
        } finally {
            isLoadingAccounts.current = false;
        }
    }, [accounts, accountsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching GTM containers using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the GTM containers
     * @returns {Promise<Array>} Promise resolving to an array of GTM containers
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchContainers = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingContainers.current) {
            return containers;
        }

        // Check if we already have the data and can return it
        if (containersLoaded && containers !== null && !forceRefresh) {
            return containers;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedContainers = await getCachedData(CACHE_KEYS.CONTAINERS);
            if (cachedContainers && cachedContainers.length > 0) {
                setContainers(cachedContainers);
                setContainersLoaded(true);
                setContainersError(null);
                return cachedContainers;
            }
        }

        // Set loading flag
        isLoadingContainers.current = true;

        // Track performance
        const startTime = performance.now();

        try {
            const containersData = await fetchContainersFromApi(forceRefresh);

            // Log performance
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${containersData.length} containers in ${duration.toFixed(2)}ms`);

            // Update state
            setContainers(containersData);
            setContainersLoaded(true);
            setContainersError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.CONTAINERS, containersData);

            return containersData;
        } catch (error) {
            // Improved error logging with status code if available
            if (error.response) {
                console.error(`[GTM Context]: Error fetching containers (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GTM Context]: Error fetching containers - No response received:', error.message);
            } else {
                console.error('[GTM Context]: Error fetching containers:', error.message);
            }

            setContainersError(error.response?.data?.error?.text || error.message || 'Failed to fetch containers');
            return [];
        } finally {
            isLoadingContainers.current = false;
        }
    }, [containers, containersLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching GTM container users using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the GTM container users
     * @returns {Promise<Array>} Promise resolving to an array of GTM container users
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchContainerUsers = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingContainerUsers.current) {
            return containerUsers;
        }

        // Check if we already have the data and can return it
        if (containerUsersLoaded && containerUsers !== null && containerUsers.length > 0 && !forceRefresh) {
            return containerUsers;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedContainerUsers = await getCachedData(CACHE_KEYS.CONTAINER_USERS);
            if (cachedContainerUsers && cachedContainerUsers.length > 0) {
                setContainerUsers(cachedContainerUsers);
                setContainerUsersLoaded(true);
                setContainerUsersError(null);
                return cachedContainerUsers;
            }
        }

        // Set loading flag
        isLoadingContainerUsers.current = true;

        // Track performance
        const startTime = performance.now();

        try {
            const containerUsersData = await fetchContainerUsersFromApi(forceRefresh);

            // Log performance
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${containerUsersData.length} container users in ${duration.toFixed(2)}ms`);

            // Update state
            setContainerUsers(containerUsersData);
            setContainerUsersLoaded(true);
            setContainerUsersError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.CONTAINER_USERS, containerUsersData);

            return containerUsersData;
        } catch (error) {
            // Improved error logging with status code if available
            if (error.response) {
                console.error(`[GTM Context]: Error fetching container users (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GTM Context]: Error fetching container users - No response received:', error.message);
            } else {
                console.error('[GTM Context]: Error fetching container users:', error.message);
            }

            setContainerUsersError(error.response?.data?.error?.text || error.message || 'Failed to fetch container users');
            return [];
        } finally {
            isLoadingContainerUsers.current = false;
        }
    }, [containerUsers, containerUsersLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching GTM container audit logs using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the GTM container audit logs
     * @returns {Promise<Array>} Promise resolving to an array of GTM container audit logs
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchContainerAuditLogs = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingContainerAuditLogs.current) {
            return containerAuditLogs;
        }

        // Check if we already have the data and can return it
        if (containerAuditLogsLoaded && containerAuditLogs !== null && !forceRefresh) {
            return containerAuditLogs;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedLogs = await getCachedData(CACHE_KEYS.CONTAINER_AUDIT_LOGS);
            if (cachedLogs && cachedLogs.length > 0) {
                setContainerAuditLogs(cachedLogs);
                setContainerAuditLogsLoaded(true);
                setContainerAuditLogsError(null);
                return cachedLogs;
            }
        }
        isLoadingContainerAuditLogs.current = true;

        const startTime = performance.now();
        try {
            const logsData = await fetchContainerAuditLogsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${logsData.length} container audit logs in ${duration.toFixed(2)}ms`);

            // Update state
            setContainerAuditLogs(logsData);
            setContainerAuditLogsLoaded(true);
            setContainerAuditLogsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.CONTAINER_AUDIT_LOGS, logsData);

            return logsData;
        } catch (error) {
            if (error.response) {
                console.error(`[GTM Context]: Error fetching container audit logs (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GTM Context]: Error fetching container audit logs - No response received:', error.message);
            } else {
                console.error('[GTM Context]: Error fetching container audit logs:', error.message);
            }

            setContainerAuditLogsError(error.response?.data?.error?.text || error.message || 'Failed to fetch container audit logs');
            return [];
        } finally {
            isLoadingContainerAuditLogs.current = false;
        }
    }, [containerAuditLogs, containerAuditLogsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching pending requests using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the pending requests
     * @returns {Promise<Array>} Promise resolving to an array of pending requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchPendingRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingPendingRequests.current) {
            return pendingRequests;
        }

        // Check if we already have the data and can return it
        if (pendingRequestsLoaded && pendingRequests !== null && !forceRefresh) {
            return pendingRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.PENDING_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setPendingRequests(cachedRequests);
                setPendingRequestsLoaded(true);
                setPendingRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingPendingRequests.current = true;

        const startTime = performance.now();
        try {
            const requestsData = await fetchPendingRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${requestsData.length} pending requests in ${duration.toFixed(2)}ms`);

            // Update state
            setPendingRequests(requestsData);
            setPendingRequestsLoaded(true);
            setPendingRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.PENDING_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GTM Context]: Error fetching pending requests:', error);
            setPendingRequestsError(error.message || 'Failed to fetch pending requests');
            return [];
        } finally {
            isLoadingPendingRequests.current = false;
        }
    }, [pendingRequests, pendingRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching approved requests using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the approved requests
     * @returns {Promise<Array>} Promise resolving to an array of approved requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchApprovedRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingApprovedRequests.current) {
            return approvedRequests;
        }

        // Check if we already have the data and can return it
        if (approvedRequestsLoaded && approvedRequests !== null && !forceRefresh) {
            return approvedRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.APPROVED_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setApprovedRequests(cachedRequests);
                setApprovedRequestsLoaded(true);
                setApprovedRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingApprovedRequests.current = true;

        const startTime = performance.now();
        try {
            const requestsData = await fetchApprovedRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${requestsData.length} approved requests in ${duration.toFixed(2)}ms`);

            // Update state
            setApprovedRequests(requestsData);
            setApprovedRequestsLoaded(true);
            setApprovedRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.APPROVED_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GTM Context]: Error fetching approved requests:', error);
            setApprovedRequestsError(error.message || 'Failed to fetch approved requests');
            return [];
        } finally {
            isLoadingApprovedRequests.current = false;
        }
    }, [approvedRequests, approvedRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching rejected requests using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the rejected requests
     * @returns {Promise<Array>} Promise resolving to an array of rejected requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchRejectedRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingRejectedRequests.current) {
            return rejectedRequests;
        }

        // Check if we already have the data and can return it
        if (rejectedRequestsLoaded && rejectedRequests !== null && !forceRefresh) {
            return rejectedRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.REJECTED_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setRejectedRequests(cachedRequests);
                setRejectedRequestsLoaded(true);
                setRejectedRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingRejectedRequests.current = true;
        
        const startTime = performance.now();
        try {
            const requestsData = await fetchRejectedRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${requestsData.length} rejected requests in ${duration.toFixed(2)}ms`);

            // Update state
            setRejectedRequests(requestsData);
            setRejectedRequestsLoaded(true);
            setRejectedRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.REJECTED_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GTM Context]: Error fetching rejected requests:', error);
            setRejectedRequestsError(error.message || 'Failed to fetch rejected requests');
            return [];
        } finally {
            isLoadingRejectedRequests.current = false;
        }
    }, [rejectedRequests, rejectedRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching pending container requests using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the pending container requests
     * @returns {Promise<Array>} Promise resolving to an array of pending container requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchPendingContainerRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingPendingContainerRequests.current) {
            return pendingContainerRequests;
        }

        // Check if we already have the data and can return it
        if (pendingContainerRequestsLoaded && pendingContainerRequests !== null && !forceRefresh) {
            return pendingContainerRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.PENDING_CONTAINER_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setPendingContainerRequests(cachedRequests);
                setPendingContainerRequestsLoaded(true);
                setPendingContainerRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingPendingContainerRequests.current = true;

        const startTime = performance.now();
        try {
            const requestsData = await fetchPendingContainerRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${requestsData.length} pending container requests in ${duration.toFixed(2)}ms`);

            // Update state
            setPendingContainerRequests(requestsData);
            setPendingContainerRequestsLoaded(true);
            setPendingContainerRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.PENDING_CONTAINER_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GTM Context]: Error fetching pending container requests:', error);
            setPendingContainerRequestsError(error.message || 'Failed to fetch pending container requests');
            return [];
        } finally {
            isLoadingPendingContainerRequests.current = false;
        }
    }, [pendingContainerRequests, pendingContainerRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching approved container requests using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the approved container requests
     * @returns {Promise<Array>} Promise resolving to an array of approved container requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchApprovedContainerRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingApprovedContainerRequests.current) {
            return approvedContainerRequests;
        }

        // Check if we already have the data and can return it
        if (approvedContainerRequestsLoaded && approvedContainerRequests !== null && !forceRefresh) {
            return approvedContainerRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.APPROVED_CONTAINER_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setApprovedContainerRequests(cachedRequests);
                setApprovedContainerRequestsLoaded(true);
                setApprovedContainerRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingApprovedContainerRequests.current = true;

        const startTime = performance.now();
        try {
            const requestsData = await fetchApprovedContainerRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${requestsData.length} approved container requests in ${duration.toFixed(2)}ms`);

            // Update state
            setApprovedContainerRequests(requestsData);
            setApprovedContainerRequestsLoaded(true);
            setApprovedContainerRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.APPROVED_CONTAINER_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GTM Context]: Error fetching approved container requests:', error);
            setApprovedContainerRequestsError(error.message || 'Failed to fetch approved container requests');
            return [];
        } finally {
            isLoadingApprovedContainerRequests.current = false;
        }
    }, [approvedContainerRequests, approvedContainerRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching rejected container requests using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the rejected container requests
     * @returns {Promise<Array>} Promise resolving to an array of rejected container requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchRejectedContainerRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingRejectedContainerRequests.current) {
            return rejectedContainerRequests;
        }

        // Check if we already have the data and can return it
        if (rejectedContainerRequestsLoaded && rejectedContainerRequests !== null && !forceRefresh) {
            return rejectedContainerRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.REJECTED_CONTAINER_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setRejectedContainerRequests(cachedRequests);
                setRejectedContainerRequestsLoaded(true);
                setRejectedContainerRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingRejectedContainerRequests.current = true;
        
        const startTime = performance.now();
        try {
            const requestsData = await fetchRejectedContainerRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GTM Context]: Fetched ${requestsData.length} rejected container requests in ${duration.toFixed(2)}ms`);

            // Update state
            setRejectedContainerRequests(requestsData);
            setRejectedContainerRequestsLoaded(true);
            setRejectedContainerRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.REJECTED_CONTAINER_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GTM Context]: Error fetching rejected container requests:', error);
            setRejectedContainerRequestsError(error.message || 'Failed to fetch rejected container requests');
            return [];
        } finally {
            isLoadingRejectedContainerRequests.current = false;
        }
    }, [rejectedContainerRequests, rejectedContainerRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for refreshing all data at once using the utils GTM API service
     * @param {boolean} forceRefresh - Whether to force a refresh of all data
     * @returns {Promise<boolean>} Promise resolving to a boolean indicating if the refresh was successful
     */
    /* -------------------------------------------------------------------------------------------- */
    const refreshAll = useCallback(async (forceRefresh = true) => {
        // Track performance
        const startTime = performance.now();

        // Run all data refreshes in parallel for better performance
        const promises = [
            fetchUsers(forceRefresh),
            fetchAuditLogs(forceRefresh),
            fetchAccounts(forceRefresh),
            fetchContainers(forceRefresh),
            fetchContainerUsers(forceRefresh),
            fetchContainerAuditLogs(forceRefresh),
            fetchPendingRequests(forceRefresh),
            fetchApprovedRequests(forceRefresh),
            fetchRejectedRequests(forceRefresh),
            fetchPendingContainerRequests(forceRefresh),
            fetchApprovedContainerRequests(forceRefresh),
            fetchRejectedContainerRequests(forceRefresh)
        ];

        // Wait for all to complete
        const results = await Promise.all(promises);

        const duration = performance.now() - startTime;
        console.log(`[GTM Context]: Refreshed all GTM data in ${duration.toFixed(2)}ms`);

        return true;
    }, [fetchUsers, fetchAuditLogs, fetchAccounts, fetchContainers, fetchContainerUsers, fetchContainerAuditLogs, fetchPendingRequests, fetchApprovedRequests, fetchRejectedRequests, fetchPendingContainerRequests, fetchApprovedContainerRequests, fetchRejectedContainerRequests]);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper & useEffect for eager loading data when user is authenticated
     * @returns {void}
     */
    /* -------------------------------------------------------------------------------------------- */
    useEffect(() => {
        // Skip if no user or already loaded
        if (!isAuthenticated || !user?.email || hasLoadedInitialData.current) {
            return;
        }

        // Wait for permissions to load, then check if user has gtm admin access
        if (permissionsLoading) {
            console.log('[GTM Context]: Waiting for permissions to load...');
            return;
        }

        // If user doesn't have gtm admin permission, skip all data fetching
        if (!hasGtmPermission) {
            console.log('[GTM Context]: User lacks admin/gtm permission, skipping data fetch');

            // Set flag to prevent re-triggering
            hasLoadedInitialData.current = true;

            // Mark all data as "loaded" with empty values to prevent loading spinners
            setUsersLoaded(true);
            setAuditLogsLoaded(true);
            setAccountsLoaded(true);
            setContainersLoaded(true);
            setContainerUsersLoaded(true);
            setContainerAuditLogsLoaded(true);
            setPendingRequestsLoaded(true);
            setApprovedRequestsLoaded(true);
            setRejectedRequestsLoaded(true);
            setPendingContainerRequestsLoaded(true);
            setApprovedContainerRequestsLoaded(true);
            setRejectedContainerRequestsLoaded(true);

            // Exit early so no API calls will be made
            return;
        }

        const loadInitialData = async () => {
            try {
                // Set flag to prevent multiple loads
                hasLoadedInitialData.current = true;

                // Track performance
                const startTime = performance.now();

                // Check if we have any cached data first
                const cachedUsers = await getCachedData(CACHE_KEYS.USERS);
                const cachedAuditLogs = await getCachedData(CACHE_KEYS.AUDIT_LOGS);
                const cachedAccounts = await getCachedData(CACHE_KEYS.ACCOUNTS);
                const cachedContainers = await getCachedData(CACHE_KEYS.CONTAINERS);
                const cachedPendingRequests = await getCachedData(CACHE_KEYS.PENDING_REQUESTS);
                const cachedApprovedRequests = await getCachedData(CACHE_KEYS.APPROVED_REQUESTS);
                const cachedRejectedRequests = await getCachedData(CACHE_KEYS.REJECTED_REQUESTS);
                const cachedPendingContainerRequests = await getCachedData(CACHE_KEYS.PENDING_CONTAINER_REQUESTS);

                // Set any cached data we find to prevent flash of loading state
                if (cachedUsers?.length > 0) {
                    setUsers(cachedUsers);
                    setUsersLoaded(true);
                }

                if (cachedAuditLogs?.length > 0) {
                    setAuditLogs(cachedAuditLogs);
                    setAuditLogsLoaded(true);
                }

                if (cachedAccounts?.length > 0) {
                    setAccounts(cachedAccounts);
                    setAccountsLoaded(true);
                }

                if (cachedContainers?.length > 0) {
                    setContainers(cachedContainers);
                    setContainersLoaded(true);
                }

                if (cachedPendingRequests?.length !== undefined) { // Accept empty arrays
                    setPendingRequests(cachedPendingRequests || []);
                    setPendingRequestsLoaded(true);
                }

                if (cachedApprovedRequests?.length !== undefined) { // Accept empty arrays
                    setApprovedRequests(cachedApprovedRequests || []);
                    setApprovedRequestsLoaded(true);
                }

                if (cachedRejectedRequests?.length !== undefined) { // Accept empty arrays
                    setRejectedRequests(cachedRejectedRequests || []);
                    setRejectedRequestsLoaded(true);
                }

                if (cachedPendingContainerRequests?.length !== undefined) { // Accept empty arrays
                    setPendingContainerRequests(cachedPendingContainerRequests || []);
                    setPendingContainerRequestsLoaded(true);
                }

                // Always fetch fresh data in the background, regardless of cache
                // Create individual timeouts for each data type to prevent blocking on slow endpoints
                const loadWithTimeout = async (fetchFn, dataType, timeoutMs = 90000) => {
                    try {
                        // Create a timeout promise that won't cause unhandled promise rejections
                        const timeoutPromise = new Promise((_, reject) => {
                            const timeoutId = setTimeout(() => {
                                clearTimeout(timeoutId);
                                console.warn(`[GTM Context]: ${dataType} data loading approaching timeout (${timeoutMs/1000}s) - will continue in background`);
                                reject({ isTimeout: true, message: `Timeout reached for ${dataType}` });
                            }, timeoutMs);
                        });

                        try {
                            // Race the actual fetch against the timeout
                            const result = await Promise.race([fetchFn(true), timeoutPromise]);
                            return result;
                        } catch (err) {
                            // Suppress timeout errors to prevent console errors
                            if (err?.isTimeout) {
                                return null;
                            }
                            // Pass through other errors
                            throw err;
                        }
                    } catch (err) {
                        console.error(`[GTM Context]: Error loading ${dataType} data:`, err);
                        return null;
                    }
                };

                // Load all data types in parallel
                const promises = [
                    loadWithTimeout(fetchUsers, 'users'),
                    loadWithTimeout(fetchAuditLogs, 'auditLogs'),
                    loadWithTimeout(fetchAccounts, 'accounts'),
                    loadWithTimeout(fetchContainers, 'containers'),
                    loadWithTimeout(fetchPendingRequests, 'pendingRequests'),
                    loadWithTimeout(fetchApprovedRequests, 'approvedRequests'),
                    loadWithTimeout(fetchRejectedRequests, 'rejectedRequests'),
                    loadWithTimeout(fetchPendingContainerRequests, 'pendingContainerRequests')
                ];

                // Wait for all data types to either load or timeout
                const results = await Promise.allSettled(promises);

                // For pending requests, even if the array is empty, consider it loaded
                if (results[4].status === 'fulfilled' && results[4].value !== null) {
                    setPendingRequests(results[4].value || []);
                    setPendingRequestsLoaded(true);
                }

                // For approved requests, even if the array is empty, consider it loaded
                if (results[5].status === 'fulfilled' && results[5].value !== null) {
                    setApprovedRequests(results[5].value || []);
                    setApprovedRequestsLoaded(true);
                }

                // For rejected requests, even if the array is empty, consider it loaded
                if (results[6].status === 'fulfilled' && results[6].value !== null) {
                    setRejectedRequests(results[6].value || []);
                    setRejectedRequestsLoaded(true);
                }

                // For pending container requests, even if the array is empty, consider it loaded
                if (results[7].status === 'fulfilled' && results[7].value !== null) {
                    setPendingContainerRequests(results[7].value || []);
                    setPendingContainerRequestsLoaded(true);
                }

                // Log any failed requests
                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        const dataType = ['users', 'auditLogs', 'accounts', 'containers', 'pendingRequests', 'approvedRequests', 'rejectedRequests', 'pendingContainerRequests'][index];
                        console.error(`[GTM Context]: Failed to fetch ${dataType} data:`, result.reason);
                    }
                });

                // Log performance
                const duration = performance.now() - startTime;
                console.log(`[GTM Context]: Initial data loading complete in ${duration.toFixed(2)}ms`);
            } catch (err) {
                console.error('[GTM Context]: Error during initial data loading:', err);
            }
        };

        // Start loading data
        loadInitialData();
    }, [isAuthenticated, user?.email, permissionsLoading, hasGtmPermission]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper & separate useEffect for marking context as initialized 
     * when auth state is available. This allows components to know when they can 
     * safely use the context.
     * @param {boolean} initialized - Whether the context is initialized
     * @param {boolean} isAuthenticated - Whether the user is authenticated
     * @returns {void}
     */
    /* -------------------------------------------------------------------------------------------- */
    useEffect(() => {
        if (!initialized && isAuthenticated !== null) {
            console.log('[GTM Context]: Context initialized with auth state:', isAuthenticated ? 'authenticated' : 'not authenticated');
            setInitialized(true);
        }
    }, [isAuthenticated, initialized]);

    // Prepare context value
    const contextValue = {
        // User data
        users,
        usersLoaded,
        usersError,

        // Audit logs
        auditLogs,
        auditLogsLoaded,
        auditLogsError,

        // GTM accounts
        accounts,
        accountsLoaded,
        accountsError,

        // GTM containers
        containers,
        containersLoaded,
        containersError,

        // GTM container users
        containerUsers,
        containerUsersLoaded,
        containerUsersError,

        // GTM container audit logs
        containerAuditLogs,
        containerAuditLogsLoaded,
        containerAuditLogsError,

        // Pending requests
        pendingRequests,
        pendingRequestsLoaded,
        pendingRequestsError,

        // Approved requests
        approvedRequests,
        approvedRequestsLoaded,
        approvedRequestsError,

        // Rejected requests
        rejectedRequests,
        rejectedRequestsLoaded,
        rejectedRequestsError,

        // Pending container requests
        pendingContainerRequests,
        pendingContainerRequestsLoaded,
        pendingContainerRequestsError,

        // Approved container requests
        approvedContainerRequests,
        approvedContainerRequestsLoaded,
        approvedContainerRequestsError,

        // Rejected container requests
        rejectedContainerRequests,
        rejectedContainerRequestsLoaded,
        rejectedContainerRequestsError,

        // Actions
        fetchUsers,
        fetchAuditLogs,
        fetchAccounts,
        fetchContainers,
        fetchContainerUsers,
        fetchContainerAuditLogs,
        fetchPendingRequests,
        fetchApprovedRequests,
        fetchRejectedRequests,
        fetchPendingContainerRequests,
        fetchApprovedContainerRequests,
        fetchRejectedContainerRequests,
        refreshAll,

        // Access operations (to be called from UI)
        createAccessBindingFromApi,
        removeAccessBindingFromApi,
        modifyAccessBindingFromApi,
        approvePendingRequestFromApi,
        rejectPendingRequestFromApi,
        createContainerAccessBindingFromApi,
        removeContainerAccessBindingFromApi,
        modifyContainerAccessBindingFromApi,
        approveContainerPendingRequestFromApi,
        rejectContainerPendingRequestFromApi,

        // Context state
        initialized
    };

    // Render provider with value
    return (
        <GtmContext.Provider value={contextValue}>
            {children}
        </GtmContext.Provider>
    );
};

// Custom hook to use the GTM context
export const useGtm = () => {
    const context = useContext(GtmContext);
    if (!context) {
        throw new Error('useGtm must be used within a GtmProvider');
    }
    return context;
};

export default GtmContext;
